import { isAuthError } from '@/errors';
import { createLogger } from '@/logger';
import type { IEventStore, ITransport } from '@/types';

const log = createLogger('EventQueue');

const DEFAULT_FLUSH_INTERVAL_MS = 500;
const DEFAULT_MAX_BUFFER_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
// Cap on a single network batch. Many proxies/servers reject bodies above a
// couple of MB, so after a long offline period the buffer is sent in chunks
// rather than one giant POST that would 413 and re-queue forever.
const DEFAULT_MAX_BATCH_BYTES = 1 * 1024 * 1024; // 1MB
const DEFAULT_MAX_BATCH_EVENTS = 1000;
// Persisting the whole buffer to storage on every `push()` re-serializes the
// entire (growing) buffer per event — O(n²) over a long offline stretch. Instead
// we coalesce writes: a hard kill loses at most this much un-persisted tail,
// which page-unload handlers flush over the network anyway.
const PERSIST_DEBOUNCE_MS = 1_000;

/**
 * Buffers recorded events and periodically flushes them through a transport.
 *
 * Responsibilities (and nothing else):
 *  - Accept incoming events from the DOM recorder
 *  - Persist the buffer to storage so events survive page navigations
 *  - Flush on interval, on page hide, on buffer size threshold, and on explicit drain()
 *  - Re-queue events on send failure so nothing is silently dropped
 *  - Clean up all listeners and timers on dispose()
 */
export class EventQueue {
  private buffer: unknown[] = [];
  /** Running estimate of the serialized buffer size in bytes (avoids O(n²) re-stringify). */
  private bufferBytes = 0;
  private flushIntervalId: ReturnType<typeof setInterval> | null = null;
  private persistTimerId: ReturnType<typeof setTimeout> | null = null;
  private readonly cleanupTasks: Array<() => void> = [];
  private flushing = false;
  private readonly maxBufferSizeBytes: number;
  private readonly maxBatchBytes: number;
  private readonly maxBatchEvents: number;

  constructor(
    private readonly transport: ITransport,
    private readonly store: IEventStore,
    private readonly flushIntervalMs: number = DEFAULT_FLUSH_INTERVAL_MS,
    private readonly unloadTransport?: ITransport,
    private readonly onAuthFailure?: () => void,
    maxBufferSizeBytes: number = DEFAULT_MAX_BUFFER_SIZE_BYTES,
    maxBatchBytes: number = DEFAULT_MAX_BATCH_BYTES,
    maxBatchEvents: number = DEFAULT_MAX_BATCH_EVENTS
  ) {
    this.maxBufferSizeBytes = maxBufferSizeBytes;
    this.maxBatchBytes = maxBatchBytes;
    this.maxBatchEvents = maxBatchEvents;
    // Recover events buffered before a previous page unload.
    this.buffer = store.load();
    this.bufferBytes = this.recomputeBufferBytes();
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  start(): void {
    // Idempotent: re-entering would double-register page-lifecycle listeners
    // and the flush interval, doubling beacon sends on unload.
    if (this.flushIntervalId !== null) return;
    this.startInterval();
    this.registerPageLifecycleListeners();
  }

  /**
   * Flush remaining events and tear down all timers and listeners.
   * Safe to call multiple times.
   */
  async drain(): Promise<void> {
    this.stopInterval();
    this.cancelPersist();
    this.removePageLifecycleListeners();
    await this.flush();
  }

  dispose(): void {
    this.stopInterval();
    this.persistNow(); // don't lose a debounced write on hard teardown
    this.removePageLifecycleListeners();
  }

  // -------------------------------------------------------------------------
  // Public interface
  // -------------------------------------------------------------------------

  /**
   * Adds an event to the buffer.
   * If the buffer exceeds max size, triggers an immediate flush.
   */
  push(event: unknown): void {
    this.buffer.push(event);
    this.bufferBytes += this.estimateEventSize(event);
    this.schedulePersist();

    if (this.bufferBytes >= this.maxBufferSizeBytes) {
      log.warn(
        `Buffer size (~${this.bufferBytes} bytes) reached max (${this.maxBufferSizeBytes} bytes), flushing`
      );
      void this.flush();
      return;
    }

    // Fast-flush when a full network batch is ready, so a burst doesn't sit
    // in memory for up to flushIntervalMs when there's already enough to send.
    // `flush()` short-circuits if one is already in flight, so this is safe.
    if (this.bufferBytes >= this.maxBatchBytes || this.buffer.length >= this.maxBatchEvents) {
      void this.flush();
    }
  }

  // -------------------------------------------------------------------------
  // Persistence (debounced — see PERSIST_DEBOUNCE_MS)
  // -------------------------------------------------------------------------

  private schedulePersist(): void {
    if (this.persistTimerId !== null) return;
    const id = setTimeout(() => {
      this.persistTimerId = null;
      this.store.save(this.buffer);
    }, PERSIST_DEBOUNCE_MS);
    (id as { unref?: () => void }).unref?.();
    this.persistTimerId = id;
  }

  private cancelPersist(): void {
    if (this.persistTimerId !== null) {
      clearTimeout(this.persistTimerId);
      this.persistTimerId = null;
    }
  }

  /** Cancel any pending debounced write and persist the current buffer now. */
  private persistNow(): void {
    this.cancelPersist();
    this.store.save(this.buffer);
  }

  // -------------------------------------------------------------------------
  // Size estimation
  // -------------------------------------------------------------------------

  private estimateEventSize(event: unknown): number {
    try {
      // +1 for the array separator / wrapper overhead.
      return JSON.stringify(event).length + 1;
    } catch {
      return 1024; // fallback per-event estimate
    }
  }

  private recomputeBufferBytes(): number {
    let total = 0;
    for (const e of this.buffer) total += this.estimateEventSize(e);
    return total;
  }

  // -------------------------------------------------------------------------
  // Flush
  // -------------------------------------------------------------------------

  async flush(isUnload = false): Promise<void> {
    if (isUnload && this.flushing) {
      // A regular flush is already in flight (it owns the batch it spliced
      // out). We can't await it — the page is being torn down — so fire
      // whatever is still in the buffer through the unload-safe transport and
      // return. We deliberately do NOT await: keepalive fetch / sendBeacon are
      // allowed to outlive the document, but a pending Promise is not.
      this.flushOnUnloadBestEffort();
      return;
    }

    // Prevent concurrent flushes — a slow REST call could overlap the interval.
    if (this.flushing) return;
    if (this.buffer.length === 0) return;

    this.flushing = true;

    // Snapshot and optimistically clear. If a chunk fails we re-prepend the
    // unsent remainder so ordering is preserved on the next attempt.
    const pending = this.buffer.splice(0);
    this.bufferBytes = 0;
    this.cancelPersist();
    this.store.clear();

    const transport =
      isUnload && this.unloadTransport?.isReady() ? this.unloadTransport : this.transport;

    const chunks = this.chunkBatch(pending);
    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i] as unknown[];
        try {
          await transport.send(chunk);
        } catch (err) {
          if (isAuthError(err)) {
            log.error('Auth error from server — recorder stopped. Check your API key.');
            this.onAuthFailure?.();
            return;
          }
          log.error(`Failed to flush events via ${transport.name}, re-queuing remainder`, err);
          // Re-prepend every event not yet sent (this chunk + the rest) so
          // chronological order is intact.
          const unsent: unknown[] = [];
          for (let j = i; j < chunks.length; j++) unsent.push(...(chunks[j] as unknown[]));
          this.buffer.unshift(...unsent);
          this.bufferBytes = this.recomputeBufferBytes();
          this.enforceBufferCap();
          this.store.save(this.buffer);
          return;
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Splits a batch into sub-batches each within `maxBatchBytes` / `maxBatchEvents`.
   * A single event larger than the byte cap still goes out alone (better to try
   * and 413 once than to drop it).
   */
  private chunkBatch(events: unknown[]): unknown[][] {
    if (events.length === 0) return [];
    if (events.length <= this.maxBatchEvents) {
      let total = 0;
      for (const e of events) total += this.estimateEventSize(e);
      if (total <= this.maxBatchBytes) return [events];
    }

    const chunks: unknown[][] = [];
    let current: unknown[] = [];
    let currentBytes = 0;
    for (const e of events) {
      const size = this.estimateEventSize(e);
      if (
        current.length > 0 &&
        (current.length >= this.maxBatchEvents || currentBytes + size > this.maxBatchBytes)
      ) {
        chunks.push(current);
        current = [];
        currentBytes = 0;
      }
      current.push(e);
      currentBytes += size;
    }
    if (current.length > 0) chunks.push(current);
    return chunks;
  }

  /**
   * Last-ditch send during page unload when a normal flush is already in
   * flight. Best effort, fire-and-forget through the unload-safe transport
   * (falling back to the default one). The buffer is left in place so the
   * in-flight flush's re-queue logic isn't disturbed if it loses the race —
   * a duplicate batch is far better than a lost one.
   */
  private flushOnUnloadBestEffort(): void {
    if (this.buffer.length === 0) return;
    const transport =
      this.unloadTransport && this.unloadTransport.isReady()
        ? this.unloadTransport
        : this.transport;
    for (const chunk of this.chunkBatch(this.buffer.slice())) {
      try {
        void transport.send(chunk);
      } catch {
        /* nothing more we can do as the page goes away */
      }
    }
  }

  /**
   * Caps the buffer at `maxBufferSizeBytes` by dropping the oldest events.
   * Called after a failed flush re-prepends events onto a buffer that may
   * already hold newer ones — without this the buffer grows without bound
   * (and eventually overflows storage) whenever the backend is unreachable.
   */
  private enforceBufferCap(): void {
    if (this.bufferBytes <= this.maxBufferSizeBytes) return;

    const before = this.buffer.length;
    while (this.buffer.length > 1 && this.bufferBytes > this.maxBufferSizeBytes) {
      const drop = Math.max(1, Math.ceil(this.buffer.length * 0.1));
      const removed = this.buffer.splice(0, drop);
      for (const e of removed) this.bufferBytes -= this.estimateEventSize(e);
    }
    if (this.bufferBytes < 0) this.bufferBytes = this.recomputeBufferBytes();
    log.warn(
      `Event buffer exceeded ${this.maxBufferSizeBytes} bytes; dropped ${
        before - this.buffer.length
      } oldest events`
    );
  }

  // -------------------------------------------------------------------------
  // Interval
  // -------------------------------------------------------------------------

  private startInterval(): void {
    const intervalId = setInterval(() => void this.flush(), this.flushIntervalMs);
    (intervalId as { unref?: () => void }).unref?.();
    this.flushIntervalId = intervalId;
  }

  private stopInterval(): void {
    if (this.flushIntervalId !== null) {
      clearInterval(this.flushIntervalId);
      this.flushIntervalId = null;
    }
  }

  // -------------------------------------------------------------------------
  // Page lifecycle listeners
  // -------------------------------------------------------------------------

  private registerPageLifecycleListeners(): void {
    // beforeunload: last chance to flush before the page is torn down.
    // pagehide: fired by browsers that freeze (rather than unload) the page —
    // covers back/forward cache scenarios where beforeunload may not fire.
    const onBeforeUnload = (): void => void this.flush(true);
    window.addEventListener('beforeunload', onBeforeUnload);
    this.cleanupTasks.push(() => window.removeEventListener('beforeunload', onBeforeUnload));

    const onPageHide = (): void => void this.flush(true);
    window.addEventListener('pagehide', onPageHide);
    this.cleanupTasks.push(() => window.removeEventListener('pagehide', onPageHide));

    // visibilitychange: browsers throttle timers for hidden tabs, so flush
    // proactively when the user switches away. Use normal flush (not beacon)
    // because the page stays alive and the transports are still open.
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') void this.flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.cleanupTasks.push(() =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
    );
  }

  private removePageLifecycleListeners(): void {
    for (const task of this.cleanupTasks) {
      try {
        task();
      } catch {
        /* ignore */
      }
    }
    this.cleanupTasks.length = 0;
  }
}
